import {
  uiFlowEdgeHasMarkerMeta,
  type UiFlowEdge,
  type UiFlowNode,
  type UiFlowNodeType,
} from './flow.types';

export interface UiFlowPoint {
  x: number;
  y: number;
}

export interface UiFlowLayout {
  width: number;
  height: number;
  columnTemplate: string;
  rowTemplate: string;
  columnCenters: readonly number[];
  rowCenters: readonly number[];
}

export interface UiFlowNodeSize {
  width: number;
  height: number;
}

const GAP_WITHOUT_MARKER = 30;
const GAP_WITH_MARKER = 60;
const HORIZONTAL_PADDING = 24;
const TOP_PADDING = 64;
const BOTTOM_PADDING = 24;

/**
 * Huella renderizada de cada molécula (debe coincidir con su CSS).
 * Las líneas se recortan a estos bordes, por lo que el tramo visible
 * respeta exactamente los gaps de 30/40px. Los captions no forman
 * parte de la huella. Si un nodo es más angosto que su columna
 * (p. ej. un rombo alineado con nodos más anchos), el gap visible
 * crece por alineación; nunca se reduce.
 */
const NODE_SIZES: Readonly<Record<UiFlowNodeType, UiFlowNodeSize>> = {
  process: { width: 160, height: 44 },
  terminator: { width: 100, height: 44 },
  document: { width: 132, height: 96 },
  decision: { width: 132, height: 132 },
  connector: { width: 76, height: 76 },
  'off-page': { width: 120, height: 116 },
  io: { width: 160, height: 72 },
  note: { width: 200, height: 72 },
};

export function getFlowNodeSize(type: UiFlowNodeType): UiFlowNodeSize {
  return NODE_SIZES[type];
}

export function buildFlowLayout(
  nodes: readonly UiFlowNode[],
  edges: readonly UiFlowEdge[],
): UiFlowLayout {
  const columnCount = Math.max(1, ...nodes.map((node) => normalizeTrack(node.col)));
  const rowCount = Math.max(1, ...nodes.map((node) => normalizeTrack(node.row)));
  const columnSizes = Array.from({ length: columnCount }, () => 0);
  const rowSizes = Array.from({ length: rowCount }, () => 0);

  for (const node of nodes) {
    const col = normalizeTrack(node.col) - 1;
    const row = normalizeTrack(node.row) - 1;
    const size = NODE_SIZES[node.type];
    columnSizes[col] = Math.max(columnSizes[col], size.width);
    rowSizes[row] = Math.max(rowSizes[row], size.height);
  }

  const columnGaps = Array.from(
    { length: Math.max(0, columnCount - 1) },
    () => GAP_WITHOUT_MARKER,
  );
  const rowGaps = Array.from(
    { length: Math.max(0, rowCount - 1) },
    () => GAP_WITHOUT_MARKER,
  );
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  let needsBottomReturnLane = false;

  for (const edge of edges) {
    const from = nodeById.get(edge.from);
    const to = nodeById.get(edge.to);
    if (!from || !to) continue;

    if (
      normalizeTrack(from.row) === normalizeTrack(to.row) &&
      normalizeTrack(to.col) < normalizeTrack(from.col) &&
      Math.abs(normalizeTrack(from.col) - normalizeTrack(to.col)) > 1
    ) {
      needsBottomReturnLane = true;
    }

    // Diamante con salida inferior (error a la derecha o reintento a la izquierda).
    if (
      from.type === 'decision' &&
      edge.marker === 'error' &&
      normalizeTrack(from.row) === normalizeTrack(to.row)
    ) {
      needsBottomReturnLane = true;
    }

    const requiredGap = uiFlowEdgeHasMarkerMeta(edge)
      ? GAP_WITH_MARKER
      : GAP_WITHOUT_MARKER;
    const colSpan = Math.abs(normalizeTrack(from.col) - normalizeTrack(to.col));
    const rowSpan = Math.abs(normalizeTrack(from.row) - normalizeTrack(to.row));

    // El gap ampliado solo aplica entre tracks adyacentes, donde el marker
    // se dibuja dentro de la frontera. Las conexiones largas solo atraviesan
    // las fronteras intermedias y no exigen espacio extra.
    if (colSpan === 1) {
      enlargeCrossedGaps(columnGaps, from.col, to.col, requiredGap);
    }
    if (rowSpan === 1) {
      enlargeCrossedGaps(rowGaps, from.row, to.row, requiredGap);
    }
  }

  const bottomPadding = needsBottomReturnLane
    ? BOTTOM_PADDING + 56
    : BOTTOM_PADDING;

  const columnTracks = buildTracks(
    columnSizes,
    columnGaps,
    HORIZONTAL_PADDING,
    HORIZONTAL_PADDING,
  );
  const rowTracks = buildTracks(rowSizes, rowGaps, TOP_PADDING, bottomPadding);

  return {
    width: columnTracks.total,
    height: rowTracks.total,
    columnTemplate: columnTracks.template,
    rowTemplate: rowTracks.template,
    columnCenters: columnTracks.centers,
    rowCenters: rowTracks.centers,
  };
}

export function getFlowNodeCenter(
  layout: UiFlowLayout,
  col: number,
  row: number,
): UiFlowPoint {
  return {
    x: layout.columnCenters[normalizeTrack(col) - 1] ?? HORIZONTAL_PADDING,
    y: layout.rowCenters[normalizeTrack(row) - 1] ?? TOP_PADDING,
  };
}

/** Los nodos ocupan los tracks pares; los impares son padding/gaps. */
export function getFlowGridTrack(track: number): number {
  return normalizeTrack(track) * 2;
}

function normalizeTrack(value: number): number {
  return Math.max(1, Math.trunc(value));
}

function enlargeCrossedGaps(
  gaps: number[],
  fromTrack: number,
  toTrack: number,
  requiredGap: number,
): void {
  const start = Math.min(normalizeTrack(fromTrack), normalizeTrack(toTrack)) - 1;
  const end = Math.max(normalizeTrack(fromTrack), normalizeTrack(toTrack)) - 1;

  for (let boundary = start; boundary < end; boundary += 1) {
    if (boundary < gaps.length) {
      gaps[boundary] = Math.max(gaps[boundary], requiredGap);
    }
  }
}

function buildTracks(
  sizes: readonly number[],
  gaps: readonly number[],
  leadingPadding: number,
  trailingPadding: number,
): { template: string; centers: readonly number[]; total: number } {
  const tracks: number[] = [leadingPadding];
  const centers: number[] = [];
  let cursor = leadingPadding;

  sizes.forEach((size, index) => {
    centers.push(cursor + size / 2);
    tracks.push(size);
    cursor += size;

    if (index < gaps.length) {
      tracks.push(gaps[index]);
      cursor += gaps[index];
    }
  });

  tracks.push(trailingPadding);
  cursor += trailingPadding;

  return {
    template: tracks.map((track) => `${track}px`).join(' '),
    centers,
    total: cursor,
  };
}
